import { useState, useEffect } from 'react'
import { useAuth, userRoles } from './useAuth'
import api from '../services/api';

export function useFunding() {
  const { user } = useAuth()
  const [fundingRequests, setFundingRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchFundingRequests = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await api.get('funder/requests/');

      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = data.map(req => ({
        ...req,
        requestedAmount: req.amount,
        requestedBy: req.requester_id,
        projectId: req.project_id,
        expectedROI: req.expected_roi,   // map snake_case → the camelCase the UI reads
      }));

      setFundingRequests(mapped);
      setError(null);
    } catch (err) {
      console.error("Fetch funding error:", err);
      // If 403 (Consultant?), maybe empty list
      if (err.response && err.response.status === 403) {
        setFundingRequests([]);
      } else {
        setError("Failed to fetch funding requests");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFundingRequests()
    }
  }, [user])

  const createFundingRequest = async (requestData) => {
    // requestData: { projectId, amount, justification, ... }
    try {
      setLoading(true)
      const payload = {
        amount: parseFloat(requestData.requestedAmount),
        justification: requestData.businessJustification,
        project_id: requestData.projectId
      };

      // POST /projects/{project_id}/funding/
      const response = await api.post(`/projects/${requestData.projectId}/funding/`, payload);

      const newRequest = {
        ...response.data,
        requestedAmount: response.data.amount,
        status: 'Pending'
      };

      setFundingRequests(prev => [...prev, newRequest])
      return { success: true, request: newRequest }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  const approveFundingRequest = async (requestId, approvalData) => {
    try {
      setLoading(true)
      // POST /funder/requests/{request_id}/decide?approve=true
      const isApproved = approvalData.decision === 'approve';

      await api.post(`/funder/requests/${requestId}/decide/`, null, {
        params: { approve: isApproved }
      });

      // Update local state
      setFundingRequests(prev =>
        prev.map(request =>
          request.id === requestId
            ? { ...request, status: isApproved ? 'Approved' : 'Rejected' }
            : request
        )
      )

      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  // Stubs for other methods to match interface
  const updateFundingRequest = async (requestId, updateData) => { return { success: true }; }
  const deleteFundingRequest = async (requestId) => { return { success: true }; }

  const getFundingRequestById = (requestId) => {
    return fundingRequests.find(request => request.id === parseInt(requestId))
  }

  const getFundingRequestsByStatus = (status) => {
    return fundingRequests.filter(request => request.status === status)
  }

  const getFundingStats = () => {
    const list = Array.isArray(fundingRequests) ? fundingRequests : [];
    const totalRequested = list.reduce((sum, r) => sum + (r.requestedAmount || 0), 0)
    const totalApproved = list
      .filter(r => r.status === 'Approved')
      .reduce((sum, r) => sum + (r.requestedAmount || 0), 0) 

    const pending = list.filter(r => r.status === 'Pending').length
    const approved = list.filter(r => r.status === 'Approved').length
    const rejected = list.filter(r => r.status === 'Rejected').length

    return {
      total: list.length,
      pending,
      approved,
      rejected,
      underReview: list.filter(r => r.status === 'under Review').length,
      totalRequested,
      totalApproved,
      approvalRate: list.length > 0 ? Math.round((approved / list.length) * 100) : 0,
      pendingValue: list
        .filter(r => r.status === 'Pending')
        .reduce((sum, r) => sum + (r.requestedAmount || 0), 0)
    }
  }

  const canApprove = user?.role === userRoles.ADMIN || user?.role === userRoles.Funder
  const canCreate = user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER

  return {
    fundingRequests,
    loading,
    error,
    createFundingRequest,
    updateFundingRequest,
    approveFundingRequest,
    deleteFundingRequest,
    getFundingRequestById,
    getFundingRequestsByStatus,
    getFundingStats,
    canApprove,
    canCreate,
    refetch: fetchFundingRequests
  }
}
